import React, { ReactNode, createContext, useContext, useState } from "react"

export const SIDEBAR_WIDTH_EXPANDED = 256
export const SIDEBAR_WIDTH_COLLAPSED = 48

/**
 * Chrome height, shared by the sidebar header, the sidebar footer and the top app
 * bar so their dividers land on the same line. (Tailwind spacing here is 2px per
 * unit, so `h-28` is 56px.)
 */
export const APP_HEADER_HEIGHT_CLASS = "h-28 min-h-28"

/**
 * Horizontal inset for the header/footer bars. Puts their content on the same
 * 12px line as the nav icons — which is also the centre of the collapsed rail, so
 * icons do not shift when the sidebar folds.
 */
export const SIDEBAR_INSET_CLASS = "px-6"

/**
 * Nav rows are pills, so their left inset splits into an outer margin (the gutter
 * around the pill) and inner padding. The two always sum to SIDEBAR_INSET_CLASS,
 * which is what keeps icons on the same line as the sidebar folds.
 *
 * Expanded trades padding for margin to widen the gutter, so the pill does not
 * crowd the sidebar's right border. Collapsed cannot: an 8px gutter would leave
 * the 48px rail with only 32px of pill, too little for the 24px icon slot plus
 * padding, so it stays symmetric and centred.
 */
export const SIDEBAR_ROW_INSET_CLASS = "mx-2 px-4"
export const SIDEBAR_ROW_INSET_EXPANDED_CLASS = "mx-4 pl-2 pr-4"

export const SIDEBAR_NAV_ROW_HEIGHT_CLASS = "h-20"
export const SIDEBAR_ROW_RADIUS_CLASS = "rounded-[10px]"

/** 24px slot centred on the collapsed rail, holding a 20px glyph. */
export const SIDEBAR_ICON_SLOT_CLASS = "w-12 h-12"
export const SIDEBAR_ICON_CLASS = "w-10 h-10"

export const SIDEBAR_SECTION_TITLE_HEIGHT_CLASS = "h-10"

interface SidebarContextType {
  sidebarShow: boolean
  setSidebarShow: (show: boolean) => void
  sidebarUnfoldable: boolean
  setSidebarUnfoldable: (unfoldable: boolean) => void
  toggleSidebar: () => void
  toggleUnfoldable: () => void
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [sidebarShow, setSidebarShow] = useState(true)
  const [sidebarUnfoldable, setSidebarUnfoldable] = useState(false)

  const toggleSidebar = () => setSidebarShow((prev) => !prev)
  const toggleUnfoldable = () => setSidebarUnfoldable((prev) => !prev)

  return (
    <SidebarContext.Provider
      value={{
        sidebarShow,
        setSidebarShow,
        sidebarUnfoldable,
        setSidebarUnfoldable,
        toggleSidebar,
        toggleUnfoldable,
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (context === undefined) {
    throw new Error("useSidebar must be used within a SidebarProvider")
  }
  return context
}
